import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, CalendarCheck2, ClipboardCheck, GitBranch, ShieldCheck } from "lucide-react";
import { GeneratedSurface } from "./GeneratedVisual";
import { getLocaleFromPathname, withLocale as buildLocalePath } from "../lib/locale";
import ChannelChooser from "./ChannelChooser";

const CTA_STEPS = [
    ["01", "Diagnóstico", "Leitura do cenário, maturidade, prioridades e riscos atuais."],
    ["02", "Recomendações", "Alternativas de plataforma, modelos, governança e primeiro caso."],
    ["03", "Plano inicial", "Próximos passos, escopo do piloto e critérios para medir valor."],
];

const CTA_COPY_BY_ROUTE = {
    default: {
        eyebrow: "Próximo passo",
        title: "Em 1 hora, saia com um plano inicial para adotar IA.",
        body:
            "A primeira conversa deve produzir uma decisão útil: onde a IA entra, onde ela para, quem assume exceções e qual evidência fica.",
        boardLabel: "Diagnóstico gratuito · 1 hora",
        boardTitle: "O que você recebe na conversa",
        primary: "Agendar diagnóstico gratuito",
        secondary: "Converse agora com nossa IA",
        tone: "default",
        steps: CTA_STEPS,
    },
    solution: {
        eyebrow: "Piloto do fluxo",
        title: "Transforme este fluxo em uma operação mensurável.",
        body:
            "Levamos uma fila, carteira, chamada ou rotina para um desenho de entrada, política, handoff e evidência antes do go-live.",
        boardLabel: "Sessão de desenho",
        boardTitle: "Do problema ao piloto",
        primary: "Diagnosticar este fluxo",
        secondary: "Falar com especialista",
        tone: "flow",
        steps: [
            ["01", "Entrada", "Onde o trabalho chega hoje e quem sente o atraso."],
            ["02", "Regra", "Qual decisão pode ser automatizada e qual precisa de pessoa."],
            ["03", "Medição", "Volume, SLA, custo e qualidade para provar valor."],
        ],
    },
    industry: {
        eyebrow: "Caso do setor",
        title: "Escolha um caso com risco claro e dono operacional.",
        body:
            "O setor muda a restrição. A primeira decisão continua sendo o fluxo: entrada, regra, responsável, prova e próxima ação.",
        boardLabel: "Recorte de setor",
        boardTitle: "O que precisamos levar",
        primary: "Mapear caso do setor",
        secondary: "Falar com especialista",
        tone: "sector",
        steps: [
            ["01", "Risco", "Privacidade, cobrança, SLA, exceção ou qualidade."],
            ["02", "Operador", "Quem responde pelo fluxo quando o agente para."],
            ["03", "Registro", "O que precisa ficar auditável depois da execução."],
        ],
    },
    trust: {
        eyebrow: "Fronteira de controle",
        title: "Revise permissão, política e fallback antes da escala.",
        body:
            "A conversa certa aqui não é sobre mais automação. É sobre quais agentes podem agir, com quais dados e quando uma pessoa assume.",
        boardLabel: "Controle",
        boardTitle: "Contrato operacional",
        primary: "Revisar fronteiras",
        secondary: "Agendar conversa",
        tone: "trust",
        steps: [
            ["01", "Permissão", "Papel, canal, operação e dado autorizado."],
            ["02", "Fallback", "Baixa confiança, risco ou exceção com dono."],
            ["03", "Auditoria", "Registro suficiente para revisar a decisão."],
        ],
    },
    implementation: {
        eyebrow: "Go-live controlado",
        title: "Lance pequeno o bastante para medir e sério o bastante para importar.",
        body:
            "O primeiro fluxo precisa de escopo estreito, responsável claro, critério de revisão e rotina de melhoria desde a primeira semana.",
        boardLabel: "Plano de lançamento",
        boardTitle: "Primeiras duas semanas",
        primary: "Planejar go-live",
        secondary: "Falar com especialista",
        tone: "implementation",
        steps: [
            ["01", "Dia 0", "Fluxo, política, integração e dono definidos."],
            ["02", "Semana 1", "Revisão de exceções e qualidade do agente."],
            ["03", "Semana 2", "Decisão de ajustar, pausar ou ampliar."],
        ],
    },
};

const ctaKeyForPath = (pathname) => {
    if (pathname.includes("/solutions/")) return "solution";
    if (pathname.includes("/industries")) return "industry";
    if (pathname.includes("/trust") || pathname.includes("/governanca")) return "trust";
    if (pathname.includes("/implementation")) return "implementation";
    return "default";
};

const CTASection = () => {
    const { pathname } = useLocation();
    const locale = getLocaleFromPathname(pathname);
    const copy = CTA_COPY_BY_ROUTE[ctaKeyForPath(pathname)] || CTA_COPY_BY_ROUTE.default;
    const to = (path) => buildLocalePath(path, locale);

    return (
        <section data-testid="cta-section" className={`zild-conversion-cta zild-conversion-cta--${copy.tone}`}>
            <div className="zild-container">
                <div className="zild-conversion-cta__grid">
                    <div className="zild-conversion-cta__copy">
                        <span>{copy.eyebrow}</span>
                        <h2>{copy.title}</h2>
                        <p>{copy.body}</p>

                        <div className="zild-conversion-cta__actions">
                            <Link
                                to={`${to("/contact")}?source=home_cta&intent=ai_adoption_diagnostic`}
                                className="zild-btn zild-btn-primary"
                                data-testid="cta-section-primary"
                            >
                                {copy.primary} <ArrowRight size={16} />
                            </Link>
                            <ChannelChooser label={copy.secondary} />
                        </div>
                    </div>

                    <GeneratedSurface
                        as="aside"
                        asset="operationsBlueprint"
                        className={`zild-conversion-cta__board zild-conversion-cta__board--${copy.tone}`}
                    >
                        <div className="zild-conversion-cta__board-top">
                            <ClipboardCheck size={18} />
                            <div>
                                <span>{copy.boardLabel}</span>
                                <strong>{copy.boardTitle}</strong>
                            </div>
                        </div>

                        <div className="zild-conversion-cta__steps">
                            {copy.steps.map(([number, title, body]) => (
                                <article key={number}>
                                    <span>{number}</span>
                                    <div>
                                        <strong>{title}</strong>
                                        <p>{body}</p>
                                    </div>
                                </article>
                            ))}
                        </div>

                        <div className="zild-conversion-cta__handoff">
                            <div>
                                <ShieldCheck size={15} />
                                <span>política antes da automação</span>
                            </div>
                            <div>
                                <GitBranch size={15} />
                                <span>handoff visível</span>
                            </div>
                            <div>
                                <CalendarCheck2 size={15} />
                                <span>próximo passo fechado</span>
                            </div>
                        </div>
                    </GeneratedSurface>
                </div>
            </div>

            <style>{`
                .zild-conversion-cta {
                    position: relative;
                    overflow: hidden;
                    padding: 62px 0 68px;
                    background: var(--zild-table-paper);
                    border-top: 1px solid var(--zild-border-soft);
                }

                .zild-conversion-cta--default {
                    border-top-color: rgba(255, 255, 255, 0.1);
                    background: var(--zild-aubergine-ink);
                }

                .zild-conversion-cta--default .zild-conversion-cta__copy > span {
                    color: var(--zild-gold);
                }

                .zild-conversion-cta.zild-conversion-cta--default h2 {
                    color: rgba(255, 255, 255, 0.96);
                }

                .zild-conversion-cta--default .zild-conversion-cta__copy > p {
                    color: rgba(255, 255, 255, 0.7);
                }

                .zild-conversion-cta__grid {
                    position: relative;
                    z-index: 1;
                    display: grid;
                    grid-template-columns: minmax(0, 0.95fr) minmax(340px, 0.62fr);
                    gap: 56px;
                    align-items: center;
                }

                .zild-conversion-cta__copy {
                    min-width: 0;
                }

                .zild-conversion-cta__copy > span,
                .zild-conversion-cta__board-top span,
                .zild-conversion-cta__steps article > span,
                .zild-conversion-cta__handoff span {
                    font-family: var(--zild-font-mono);
                    font-size: 10px;
                    letter-spacing: 0.09em;
                    text-transform: uppercase;
                }

                .zild-conversion-cta__copy > span {
                    color: var(--zild-action);
                }

                .zild-conversion-cta h2 {
                    max-width: 700px;
                    margin: 14px 0 0;
                    color: var(--zild-aubergine-ink);
                    font-size: clamp(1.95rem, 3.25vw, 2.82rem);
                    line-height: 1.08;
                    font-weight: 600;
                    letter-spacing: 0;
                }

                .zild-conversion-cta p {
                    max-width: 700px;
                    margin: 20px 0 0;
                    color: var(--zild-text-muted);
                    font-size: 15px;
                    line-height: 1.68;
                }

                .zild-conversion-cta__actions {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 12px;
                    margin-top: 28px;
                }

                .zild-conversion-cta__board {
                    min-width: 0;
                    padding: 24px;
                    border-radius: var(--zild-radius-lg);
                    border: 1px solid rgba(255, 255, 255, 0.14);
                    background: var(--zild-aubergine-nav);
                    color: rgba(255, 255, 255, 0.74);
                    --zild-generated-opacity: 0.22;
                    --zild-generated-position: center top;
                    --zild-generated-overlay:
                        linear-gradient(135deg, rgba(0, 29, 34, 0.96), rgba(0, 29, 34, 0.72));
                    box-shadow: var(--zild-shadow-panel-strong);
                }

                .zild-conversion-cta__board--sector {
                    --zild-generated-overlay:
                        linear-gradient(135deg, rgba(36, 54, 48, 0.96), rgba(0, 29, 34, 0.68));
                }

                .zild-conversion-cta__board--trust {
                    --zild-generated-overlay:
                        linear-gradient(135deg, rgba(30, 18, 42, 0.96), rgba(18, 64, 61, 0.74));
                }

                .zild-conversion-cta__board--implementation {
                    --zild-generated-overlay:
                        linear-gradient(135deg, rgba(0, 56, 68, 0.94), rgba(0, 29, 34, 0.76));
                }

                .zild-conversion-cta__board-top {
                    display: grid;
                    grid-template-columns: 38px minmax(0, 1fr);
                    gap: 14px;
                    align-items: start;
                }

                .zild-conversion-cta__board-top > svg {
                    width: 36px;
                    height: 36px;
                    padding: 8px;
                    border-radius: var(--zild-radius-md);
                    background: rgba(0, 194, 209, 0.13);
                    color: var(--zild-gold);
                }

                .zild-conversion-cta__board-top span {
                    display: block;
                    color: var(--zild-gold);
                }

                .zild-conversion-cta__board-top strong {
                    display: block;
                    margin-top: 7px;
                    color: rgba(255, 255, 255, 0.94);
                    font-size: 22px;
                    line-height: 1.16;
                    font-weight: 600;
                }

                .zild-conversion-cta__steps {
                    display: grid;
                    gap: 1px;
                    margin-top: 24px;
                    overflow: hidden;
                    border-radius: var(--zild-radius-md);
                    background: rgba(255, 255, 255, 0.12);
                }

                .zild-conversion-cta__steps article {
                    display: grid;
                    grid-template-columns: 38px minmax(0, 1fr);
                    gap: 12px;
                    min-width: 0;
                    padding: 16px;
                    background: rgba(255, 255, 255, 0.07);
                }

                .zild-conversion-cta__steps article > span {
                    color: rgba(0, 194, 209, 0.86);
                }

                .zild-conversion-cta__steps strong {
                    color: rgba(255, 255, 255, 0.92);
                    font-size: 14px;
                    line-height: 1.25;
                    font-weight: 600;
                }

                .zild-conversion-cta__steps p {
                    margin: 6px 0 0;
                    color: rgba(255, 255, 255, 0.64);
                    font-size: 12px;
                    line-height: 1.5;
                }

                .zild-conversion-cta__handoff {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px;
                    margin-top: 18px;
                }

                .zild-conversion-cta__handoff div {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    min-width: 0;
                    padding: 8px 9px;
                    border-radius: var(--zild-radius-sm);
                    background: rgba(255, 255, 255, 0.08);
                    color: rgba(255, 255, 255, 0.86);
                }

                .zild-conversion-cta__handoff svg {
                    color: var(--zild-gold);
                    flex: 0 0 auto;
                }

                .zild-conversion-cta__handoff span {
                    letter-spacing: 0.07em;
                }

                @media (max-width: 920px) {
                    .zild-conversion-cta {
                        padding: 50px 0 58px;
                    }

                    .zild-conversion-cta__grid {
                        grid-template-columns: 1fr;
                        gap: 30px;
                    }
                }

                @media (max-width: 560px) {
                    .zild-conversion-cta h2 {
                        font-size: clamp(1.82rem, 9vw, 2.35rem);
                    }

                    .zild-conversion-cta__actions,
                    .zild-conversion-cta__actions .zild-btn {
                        width: 100%;
                    }

                    .zild-conversion-cta__steps article {
                        grid-template-columns: 1fr;
                        gap: 8px;
                    }
                }
            `}</style>
        </section>
    );
};

export default CTASection;
